import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { components } from "api/generated";
import {
  getMemberships,
  getPlatformUserMembershipsByPlatformUserMembershipIdChangeOptions,
  postPlatformUserMembershipsByPlatformUserMembershipIdChangeMembership,
} from "api/methods";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Check from "atoms/icon/icons/ic_check_small_ol.svg";
import CheckXSmall from "atoms/icon/icons/ic_check_xsmall_ol.svg";
import Lock from "atoms/icon/icons/lock.svg";
import { Notification, useNotify } from "atoms/notification";
import { TextButton } from "atoms/text-button/TextButton";
import TopNav from "molecules/navbars/TopNav";
import { useTranslation } from "@/core/i18n";
import { queryClient } from "pages/Root";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import { formatBillingPeriodInterval, parseBillingPeriodName } from "utils/billingPeriod.helpers";

import {
  invalidateMembershipQueries,
  MEMBERSHIP_CHANGE_DIRECTION_DOWNGRADE,
  type MembershipChangeLocationState,
} from "./membershipHelper";

const load = async (platformUserMembershipId: string) => {
  const changeOptions = queryClient.fetchQuery({
    ...getPlatformUserMembershipsByPlatformUserMembershipIdChangeOptions.keyed({
      platformUserMembershipId,
    }),
  });

  // Change options carry no benefits; the location's membership catalog does.
  const memberships = queryClient.fetchQuery({ ...getMemberships.keyed() });

  return waitObj({ platformUserMembershipId, changeOptions, memberships });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipChangeLoader: LoaderFunction = async ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  const data = await load(platformUserMembershipId);

  // A change is already scheduled — the manage card owns that state.
  if (data.changeOptions.pendingChange) {
    return redirect("/wallet/memberships/manage");
  }

  return data;
};

type ChangeOption = components["schemas"]["DisplayMembershipChangeOption"];

export const WalletMembershipChangePage = () => {
  const { platformUserMembershipId, changeOptions, memberships } = useLoaderData() as LoaderData;
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [notificationProps, notify] = useNotify();

  const { currentMembership, options } = changeOptions;

  // The cancel page's downgrade offer opens straight on the confirm step for the plan picked there.
  const { preselectedMembershipId } = (useLocation().state ?? {}) as MembershipChangeLocationState;
  const preselectedOption = options.find(
    ({ membershipId, eligible }) => eligible && membershipId === preselectedMembershipId,
  );
  const [selectedMembershipId, setSelectedMembershipId] = useState(preselectedOption?.membershipId);
  const [step, setStep] = useState<"select" | "confirm">(preselectedOption ? "confirm" : "select");
  const selectedOption = options.find(({ membershipId }) => membershipId === selectedMembershipId);
  const renewalDate = dayjs(currentMembership.nextRenewalAt);

  // Undefined when the plan isn't in this location's catalog (e.g. unlisted), so no benefits are shown.
  const benefitsOf = (membershipId: string) =>
    memberships
      .find(({ id }) => id === membershipId)
      ?.membershipBenefits.map(({ description }) => description);

  const changeMembershipMutation = useMutation({
    mutationFn: (targetMembershipId: string) =>
      postPlatformUserMembershipsByPlatformUserMembershipIdChangeMembership({
        platformUserMembershipId,
        targetMembershipId,
      }),
    onSuccess: async () => {
      await invalidateMembershipQueries(queryClient);

      navigate("/wallet/memberships/manage");
    },
    onError: () => {
      notify.error(t("walletMembershipChangePage.genericError"), {
        timeout: 5000,
        dismissable: false,
      });
    },
  });

  const formatOptionPrice = (option: {
    billingPeriodCost: number;
    billingPeriodInterval: number;
    billingPeriodIntervalCount: number;
  }) =>
    formatBillingPeriodInterval(
      option.billingPeriodCost,
      parseBillingPeriodName(option.billingPeriodInterval),
      option.billingPeriodIntervalCount,
    );

  // Only comparable when both plans bill on the same cadence.
  const priceDifference = (option: ChangeOption) => {
    if (
      option.billingPeriodInterval !== currentMembership.billingPeriodInterval ||
      option.billingPeriodIntervalCount !== currentMembership.billingPeriodIntervalCount
    ) {
      return null;
    }
    const difference = option.billingPeriodCost - currentMembership.billingPeriodCost;
    if (difference === 0) {
      return null;
    }
    const price = formatOptionPrice({ ...option, billingPeriodCost: Math.abs(difference) });
    return difference > 0
      ? t("walletMembershipChangePage.priceMore", { price })
      : t("walletMembershipChangePage.priceLess", { price });
  };

  const optionRow = (option: ChangeOption) => {
    const isSelected = option.membershipId === selectedMembershipId;
    const difference = priceDifference(option);
    const optionBenefits = benefitsOf(option.membershipId)?.slice(0, 3);

    return (
      <button
        key={option.membershipId}
        role="radio"
        aria-checked={isSelected}
        aria-label={`${option.name}, ${formatOptionPrice(option)}`}
        className={`flex w-full flex-col gap-y-1 rounded-lg border p-4 text-left ${
          !option.eligible
            ? "border-light-grey opacity-60"
            : isSelected
              ? "border-brand-color bg-brand-color/10"
              : "border-light-grey"
        }`}
        disabled={!option.eligible}
        type="button"
        onClick={() => setSelectedMembershipId(option.membershipId)}
      >
        <div className="flex w-full items-center gap-x-2">
          <span className="grow truncate text-bold1">{option.name}</span>
          <span className="shrink-0 rounded-full bg-one px-3 py-1 text-body2">
            {t(
              option.direction === MEMBERSHIP_CHANGE_DIRECTION_DOWNGRADE
                ? "walletMembershipChangePage.directionDowngrade"
                : "walletMembershipChangePage.directionUpgrade",
            )}
          </span>
          {!option.eligible && <Icon svg={Lock} size="xsmall" />}
          {isSelected && <Icon svg={Check} size="xsmall" color="brandColor" />}
        </div>
        <span className="text-body2 text-secondary">
          {formatOptionPrice(option)}
          {difference && <span className="text-primary"> · {difference}</span>}
        </span>
        {option.eligible && optionBenefits && optionBenefits.length > 0 && (
          <ul className="flex flex-col gap-y-0.5 pt-1">
            {optionBenefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-x-2 text-body3 text-primary">
                <Icon svg={CheckXSmall} color="brandColor" size="x3small" />
                <span className="line-clamp-1">{benefit}</span>
              </li>
            ))}
          </ul>
        )}
        {!option.eligible && option.blockedUntil && (
          <span className="text-body2 text-secondary">
            {t("walletMembershipChangePage.availableAfter", {
              date: dayjs(option.blockedUntil).format("MMM D, YYYY"),
            })}
          </span>
        )}
      </button>
    );
  };

  const selectedBenefits = selectedOption && benefitsOf(selectedOption.membershipId);

  return (
    <div className="relative flex h-full flex-col bg-white">
      <Notification {...notificationProps} />
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => (step === "confirm" ? setStep("select") : navigate(-1))}
      >
        <div className="flex justify-center text-sub2">{t("walletMembershipChangePage.title")}</div>
      </TopNav>

      {step === "select" && (
        <div className="flex grow flex-col gap-y-6 px-6 pb-8 pt-3">
          <div className="flex flex-col gap-y-1">
            <div className="text-title">{t("walletMembershipChangePage.chooseTitle")}</div>
            <div className="text-body2 text-secondary">
              {t("walletMembershipChangePage.currentPlan", {
                planName: currentMembership.name,
                price: formatOptionPrice(currentMembership),
              })}
            </div>
          </div>
          {options.length === 0 ? (
            <div className="text-body2 text-secondary">
              {t("walletMembershipChangePage.noOptions")}
            </div>
          ) : (
            <div
              role="radiogroup"
              aria-label={t("walletMembershipChangePage.chooseTitle")}
              className="flex flex-col gap-y-3"
            >
              {options.map(optionRow)}
            </div>
          )}
          <div className="grow" />
          <Button fullWidth disabled={!selectedOption} onClick={() => setStep("confirm")}>
            {t("common.continue")}
          </Button>
        </div>
      )}

      {step === "confirm" && selectedOption && (
        <div className="flex grow flex-col gap-y-6 px-6 pb-8 pt-3">
          <div className="flex flex-col gap-y-1">
            <div className="text-title">{t("walletMembershipChangePage.confirmTitle")}</div>
            <div className="text-body2 text-secondary">
              {t("walletMembershipChangePage.fromTo", {
                fromPlan: currentMembership.name,
                toPlan: selectedOption.name,
              })}
            </div>
          </div>
          <ol className="flex flex-col gap-y-3 rounded-xl bg-one p-4">
            <li className="flex items-baseline justify-between gap-x-3 text-body2">
              <span className="text-secondary">{t("walletMembershipChangePage.today")}</span>
              <span className="text-primary">
                {t("walletMembershipChangePage.nothingCharged", { amount: formatCurrency(0) })}
              </span>
            </li>
            <li className="flex items-baseline justify-between gap-x-3 text-body2">
              <span className="shrink-0 text-secondary">{renewalDate.format("MMM D, YYYY")}</span>
              <span className="text-right text-primary">
                {t("walletMembershipChangePage.startsAtPrice", {
                  planName: selectedOption.name,
                  price: formatOptionPrice(selectedOption),
                })}
              </span>
            </li>
          </ol>
          {selectedBenefits && selectedBenefits.length > 0 && (
            <div className="flex flex-col gap-y-1">
              <span className="text-bold2 text-primary">
                {t("walletMembershipChangePage.planIncludes", { planName: selectedOption.name })}
              </span>
              {selectedBenefits.map((benefit) => (
                <span key={benefit} className="flex items-center gap-x-2 text-body2">
                  <Icon svg={CheckXSmall} color="brandColor" size="x3small" />
                  {benefit}
                </span>
              ))}
            </div>
          )}
          <div className="text-body3 text-secondary">
            {t("walletMembershipChangePage.disclaimer")}
          </div>
          <div className="grow" />
          <Button
            fullWidth
            disabled={changeMembershipMutation.isPending}
            onClick={() => changeMembershipMutation.mutate(selectedOption.membershipId)}
          >
            {t("walletMembershipChangePage.confirmChange")}
          </Button>
          <div className="flex justify-center">
            <TextButton
              variant="secondary"
              disabled={changeMembershipMutation.isPending}
              onClick={() => navigate("/wallet/memberships/manage")}
            >
              {t("walletMembershipChangePage.keepCurrent", { planName: currentMembership.name })}
            </TextButton>
          </div>
        </div>
      )}
    </div>
  );
};
